import React, { useState } from 'react';
import AssignAdvisorControl from '../../sales_task_list/advisors/AssignAdvisorControl';
import AdvisorHistoryModal from '../../sales_task_list/advisors/AdvisorHistoryModal';
import useCanManageAdvisors from '../../sales_task_list/advisors/useCanManageAdvisors';
import type { Notify } from '../../sales_task_list/advisors/AssignAdvisorModal';
import { SHARED_STYLES } from '../../sales_task_list/styles';
import salesAdvisorLabel, { hasNoAdvisor } from './sales_advisor_label';

interface Props {
  // null = nobody assigned, undefined = older server (shows nothing).
  advisor: { name?: string; source?: string; expires_at?: string } | null | undefined;
  studentUuid: string;
  studentName: string;
  isSuperUser: boolean;
  // Banned / deleted students cannot be assigned (server answers 400 STUDENT_INELIGIBLE).
  ineligible?: boolean;
  notify: Notify;
  // Reload the profile after an assign / 409 so the new advisor shows.
  onReload: () => void;
}

// Profile slot: the advisor label (or Assign, for advisor writers, when nobody holds the student),
// plus Advisor history for anyone who can view advisors.
function SalesAdvisorSlot({
  advisor,
  studentUuid,
  studentName,
  isSuperUser,
  ineligible,
  notify,
  onReload
}: Props) {
  const { canView, canWrite } = useCanManageAdvisors(isSuperUser);
  const [historyOpen, setHistoryOpen] = useState(false);
  if (advisor === undefined) return null;

  const noAdvisor = hasNoAdvisor(advisor);
  const label = noAdvisor ? null : salesAdvisorLabel(advisor);
  const showAssign = noAdvisor && !ineligible && canWrite;
  if (!label && !canView) return null;

  return (
    <div
      className="flex flex-wrap justify-center items-center gap-3"
      data-testid={noAdvisor ? 'sales-advisor-assign' : 'sales-advisor-label'}
    >
      {label ? (
        <div className="min-w-0 text-center break-words">
          <div>{label.text}</div>
          {label.detail ? <div className="text-xs">{label.detail}</div> : null}
        </div>
      ) : null}
      {showAssign ? (
        <AssignAdvisorControl
          studentUuid={studentUuid}
          studentName={studentName}
          notify={notify}
          onDone={onReload}
        />
      ) : null}
      {canView ? (
        <>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={SHARED_STYLES.SECONDARY_BTN}
          >
            Advisor history
          </button>
          <AdvisorHistoryModal
            student={historyOpen ? { uuid: studentUuid, name: studentName } : null}
            onClose={() => setHistoryOpen(false)}
          />
        </>
      ) : null}
    </div>
  );
}

export default SalesAdvisorSlot;
